/**
 * InferenceTab.jsx
 * Full inference pipeline:
 *   1. Scan for live stations (including WIS2)
 *   2. User selects station
 *   3. Activate: bridge historical + start poller + fetch forecast #1
 *   4. Progress monitoring with auto-refresh
 *   5. On completion: fetch forecast #2
 *   6. Channel mapping (auto-suggest + manual + CSV upload)
 *   7. Export: sensor.parquet + forecast_start.parquet + forecast_end.parquet
 */

import { useState, useEffect, useRef, useCallback } from "react";

// Sensor/forecast data — station discovery, activation, pollers, raw data
// status. Deployed already at api.weatherdatamanager.gwc.dm-airtech.com.
const API_BASE = "https://api.weatherdatamanager.gwc.dm-airtech.com";
// Inference wrap — weights, preview/predict, manual export, schedules,
// /infer, results. Deployed at api.sensorplus.gwc.dm-airtech.com.
const INFERENCE_API_BASE = "https://api.sensorplus.inference.gwc.dm-airtech.com";

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  bg:        "#0f1117",
  surface:   "#181c25",
  surfaceAlt:"#1e2330",
  border:    "#2a3045",
  borderHi:  "#3a4460",
  text:      "#e8ecf4",
  textSub:   "#7a8499",
  textMuted: "#4a5268",
  accent:    "#4f8ef7",
  accentDim: "#1e3a6e",
  success:   "#3ecf8e",
  successDim:"#0d3d28",
  warn:      "#f5a623",
  warnDim:   "#3d2a0a",
  danger:    "#f05252",
  dangerDim: "#3d1010",
};

// ── Helpers ────────────────────────────────────────────────────────────────────
// Sensor API (station discovery/activation/raw data status)
const api = async (path, opts = {}) => {
  const r = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(err.detail || r.statusText);
  }
  return r;
};
const apiJson = async (path, opts = {}) => (await api(path, opts)).json();

// Inference wrap (weights/preview/predict/export/schedules/infer/results)
const inferenceApi = async (path, opts = {}) => {
  const r = await fetch(`${INFERENCE_API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(err.detail || r.statusText);
  }
  return r;
};
const inferenceApiJson = async (path, opts = {}) => (await inferenceApi(path, opts)).json();

const ts = () => new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

// Recognizes inference_server.py's specific "not enough continuous history
// yet" ValueError (e.g. "need 360 minutes of sensor context (after 1-min
// resampling), only have 351") and turns it into a friendly, self-resolving
// progress message instead of showing the raw exception text. Returns null
// for any other kind of error, which callers fall back to showing normally.
function parseContinuityShortfall(msg) {
  if (!msg) return null;
  const m = msg.match(/need (\d+) minutes.*?only have (\d+)/i);
  if (!m) return null;
  return { need: parseInt(m[1], 10), have: parseInt(m[2], 10) };
}

// Recognizes inference_server.py's "gap longer than the backfill limit"
// ValueError — a genuine leading data gap too long for the bounded
// backward-fill to close on its own — and extracts the channel/minute
// detail so it can be shown as a clear list instead of a raw JSON-ish
// string. Returns null for any other error, same fallback pattern as
// parseContinuityShortfall above.
function parseLeadingGapError(msg) {
  if (!msg) return null;
  const m = msg.match(/gap longer than the (\d+)-minute backfill limit: (\{[^}]*\})/i);
  if (!m) return null;
  try {
    // The detail is Python dict repr (single quotes), not valid JSON —
    // normalize just enough to parse it.
    const detail = JSON.parse(m[2].replace(/'/g, '"'));
    return { limitMinutes: parseInt(m[1], 10), channels: detail };
  } catch {
    return { limitMinutes: parseInt(m[1], 10), channels: null };
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "1rem 1.125rem", ...style
  }}>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label style={{ fontSize: 12, color: C.textSub, display: "block", marginBottom: 4 }}>
    {children}
  </label>
);

const Err = ({ msg }) => msg ? (
  <div style={{
    background: C.dangerDim, border: `1px solid ${C.danger}`,
    borderRadius: 8, padding: "10px 14px", fontSize: 13,
    color: C.danger, marginBottom: 12
  }}>{msg}</div>
) : null;

const Spinner = ({ size = 14 }) => (
  <span style={{
    display: "inline-block", width: size, height: size,
    border: `2px solid ${C.border}`, borderTopColor: C.accent,
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
    flexShrink: 0,
  }} />
);

const pill = (label, color = C.accent, bg = C.accentDim) => (
  <span style={{
    fontSize: 10, fontWeight: 600, padding: "2px 7px",
    borderRadius: 20, background: bg, color, letterSpacing: 0.4,
    whiteSpace: "nowrap",
  }}>{label}</span>
);

const freqColor = (freq) => {
  if (!freq) return C.textSub;
  if (freq.includes("1min") || freq.includes("~1") || freq.includes("5min")) return C.success;
  if (freq.includes("10min")) return C.accent;
  if (freq.includes("20min") || freq.includes("30")) return C.warn;
  return C.textSub;
};

const ProgressBar = ({ pct, color = C.accent }) => (
  <div style={{
    height: 6, background: C.border, borderRadius: 3,
    overflow: "hidden", margin: "6px 0"
  }}>
    <div style={{
      height: "100%", width: `${Math.min(pct, 100)}%`,
      background: color, borderRadius: 3,
      transition: "width 0.5s ease",
    }} />
  </div>
);

// ── Default channel mappings ────────────────────────────────────────────────────
const SENSOR_DEFAULTS = {
  temperature:    { to: "t2m_k",            source_unit: "°C" },
  humidity:       { to: "rh",               source_unit: "%" },
  dew_point:      { to: "d2m_k",            source_unit: "°C" },
  wind_speed:     { to: "wind_speed_10m_ms", source_unit: "m/s" },
  wind_gust:      { to: "i10fg_ms",         source_unit: "m/s" },
  wind_direction: { to: "wind_dir_10m_ms",  source_unit: "degrees" },
  pressure:       { to: "sp_pa",            source_unit: "hPa" },
  solar:          { to: "ssrd_jm2",         source_unit: "W/m²" },
  rain_1h:        { to: "tp_m",             source_unit: "mm" },
};

// Field names confirmed against the real API response (GET /forecast) — this
// backend normalizes weather data into the same shape as sensor readings,
// NOT the standard Open-Meteo hourly parameter names we'd guessed before.
const FORECAST_DEFAULTS = {
  temperature:     { to: "t2m_k",              source_unit: "°C" },
  dew_point:       { to: "d2m_k",              source_unit: "°C" },
  humidity:        { to: "rh",                 source_unit: "%" },
  pressure:        { to: "sp_pa",              source_unit: "hPa" },
  wind_speed:      { to: "wind_speed_10m_ms",  source_unit: "km/h" },
  wind_direction:  { to: "wind_dir_10m_ms",    source_unit: "degrees" },
  wind_gust:       { to: "i10fg_ms",           source_unit: "km/h" },
  wind_speed_100m: { to: "wind_speed_100m_ms", source_unit: "km/h" },
  rain_1h:         { to: "tp_m",               source_unit: "mm" },
  rain:            { to: "rain_m",             source_unit: "mm" },
  cloud_cover:     { to: "tcc",                source_unit: "%" },
  cape:            { to: "cape_jkg",           source_unit: "J/kg" },
  // "solar" was null throughout, even at midday, in the raw response we
  // checked — this API doesn't appear to populate it currently. Left mapped
  // so the sensor-persistence fallback in inference_server.py can still
  // cover ssrd_jm2 from the real sensor reading instead.
  solar:           { to: "ssrd_jm2",           source_unit: "W/m²" },
};

const ML_CHANNELS = [
  { id: "t2m_k", unit: "K" }, { id: "d2m_k", unit: "K" },
  { id: "rh", unit: "%" }, { id: "sp_pa", unit: "Pa" },
  { id: "msl_pa", unit: "Pa" }, { id: "wind_speed_10m_ms", unit: "m/s" },
  { id: "wind_dir_10m_ms", unit: "degrees" }, { id: "i10fg_ms", unit: "m/s" },
  { id: "u10_ms", unit: "m/s" }, { id: "v10_ms", unit: "m/s" },
  { id: "wind_speed_100m_ms", unit: "m/s" }, { id: "u100_ms", unit: "m/s" },
  { id: "v100_ms", unit: "m/s" }, { id: "ssrd_jm2", unit: "J/m²" },
  { id: "tp_m", unit: "m" }, { id: "rain_m", unit: "m" },
  { id: "tcc", unit: "0-1" }, { id: "visibility_km", unit: "km" },
  { id: "cape_jkg", unit: "J/kg" }, { id: "feels_like", unit: "°C" },
];

const ALL_UNITS = [
  "°C", "K", "%", "m/s", "knots", "km/h", "hPa", "Pa", "inHg",
  "mm", "m", "W/m²", "J/m²", "degrees", "0-1", "J/kg", "km", "s", "min",
];

// ── Mapping table ──────────────────────────────────────────────────────────────
function unmappedFields(fields, mapping, excluded) {
  return fields.filter(f => !excluded[f] && !mapping[f]?.to);
}

function MappingTable({ title, fields, mapping, setMapping, excluded, setExcluded }) {
  const mapped   = fields.filter(f => !excluded[f] && mapping[f]?.to).length;
  const excCount = fields.filter(f => excluded[f]).length;
  const pending  = unmappedFields(fields, mapping, excluded);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: C.textSub }}>
          {title}
          <span style={{ fontWeight: 400, marginLeft: 8, fontSize: 11 }}>
            <span style={{ color: C.success }}>{mapped} mapped</span>
            {excCount > 0 && <span style={{ color: C.textMuted, marginLeft: 6 }}>{excCount} excluded</span>}
            {pending.length > 0 && (
              <span style={{ color: C.danger, marginLeft: 6 }}>
                {pending.length} need a channel or exclude
              </span>
            )}
          </span>
        </p>
        <button
          onClick={() => {
            const allEx = fields.every(f => excluded[f]);
            const next = {};
            fields.forEach(f => { next[f] = !allEx; });
            setExcluded(ex => ({ ...ex, ...next }));
          }}
          style={{ fontSize: 11, padding: "3px 10px", color: C.textSub }}
        >
          {fields.every(f => excluded[f]) ? "Include all" : "Exclude all"}
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
          <thead>
            <tr>
              {["✓", "Field", "Unit", "→", "ML channel", "Conversion"].map((h, i) => (
                <th key={i} style={{
                  fontSize: 11, fontWeight: 500, color: C.textSub,
                  padding: "5px 6px", textAlign: i < 2 ? "center" : "left",
                  borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap"
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map(field => {
              const isEx = !!excluded[field];
              const m    = mapping[field] || { to: "", source_unit: "" };
              const tgt  = ML_CHANNELS.find(c => c.id === m.to);
              const conv = tgt && m.source_unit && m.source_unit !== tgt.unit
                ? `${m.source_unit} → ${tgt.unit}`
                : tgt ? "✓" : "";
              return (
                <tr key={field} style={{ opacity: isEx ? 0.3 : 1, borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ textAlign: "center", padding: "5px 6px" }}>
                    <input type="checkbox" checked={!isEx}
                      onChange={e => setExcluded(ex => ({ ...ex, [field]: !e.target.checked }))}
                      style={{ cursor: "pointer" }} />
                  </td>
                  <td style={{ padding: "5px 6px", fontWeight: 500, color: isEx ? C.textMuted : C.text, whiteSpace: "nowrap" }}>
                    {field}
                  </td>
                  <td style={{ padding: "5px 6px" }}>
                    <select disabled={isEx} value={m.source_unit}
                      onChange={e => setMapping(mp => ({ ...mp, [field]: { ...m, source_unit: e.target.value } }))}
                      style={{ fontSize: 11, width: 80, background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "2px 4px" }}>
                      <option value="">—</option>
                      {ALL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "5px 6px", color: C.textMuted }}>→</td>
                  <td style={{ padding: "5px 6px" }}>
                    <select disabled={isEx} value={m.to}
                      onChange={e => setMapping(mp => ({ ...mp, [field]: { ...m, to: e.target.value } }))}
                      style={{ fontSize: 11, width: 160, background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, borderRadius: 4, padding: "2px 4px" }}>
                      <option value="">— channel —</option>
                      {ML_CHANNELS.map(c => <option key={c.id} value={c.id}>{c.id} ({c.unit})</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "5px 6px", fontSize: 11, color: conv === "✓" ? C.success : C.warn }}>
                    {isEx ? "excluded" : conv}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Head preview table (first few rows of sensor / forecast context) ───────────
function HeadTable({ title, rows }) {
  if (!rows || rows.length === 0) {
    return <p style={{ fontSize: 12, color: C.textSub }}>{title}: no rows returned.</p>;
  }
  const cols = Object.keys(rows[0]);
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>
        {title} <span style={{ fontWeight: 400 }}>(first {rows.length} rows)</span>
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ fontSize: 11 }}>
          <thead>
            <tr>{cols.map(c => <th key={c} style={{ whiteSpace: "nowrap" }}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {cols.map(c => (
                  <td key={c} style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                    {typeof row[c] === "number" ? row[c].toFixed(3) : String(row[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Minimal dependency-free line chart: two series over the forecast horizon ───
function MiniLineChart({ seriesA, seriesB, labelA, labelB, colorA, colorB, height = 220 }) {
  const width = 640;
  const pad = 36;
  const all = [...(seriesA || []), ...(seriesB || [])].filter(v => v != null && !Number.isNaN(v));
  const min = all.length ? Math.min(...all) : 0;
  const max = all.length ? Math.max(...all) : 1;
  const span = max - min || 1;
  const n = Math.max((seriesA || []).length, (seriesB || []).length, 1);
  const x = i => pad + (i / Math.max(n - 1, 1)) * (width - pad * 2);
  const y = v => height - pad - ((v - min) / span) * (height - pad * 2);
  const toPath = (arr) => (arr || [])
    .map((v, i) => (v == null || Number.isNaN(v) ? null : `${i === 0 ? "M" : "L"}${x(i)},${y(v)}`))
    .filter(Boolean)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke={C.border} />
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke={C.border} />
      <path d={toPath(seriesB)} stroke={colorB} strokeWidth={2} fill="none" strokeDasharray="5 4" />
      <path d={toPath(seriesA)} stroke={colorA} strokeWidth={2} fill="none" />
      <circle cx={pad + 6} cy={pad - 2} r={3} fill={colorA} />
      <text x={pad + 14} y={pad + 1} fontSize={10} fill={colorA}>{labelA}</text>
      <circle cx={pad + 150} cy={pad - 2} r={3} fill={colorB} />
      <text x={pad + 158} y={pad + 1} fontSize={10} fill={colorB}>{labelB}</text>
      <text x={4} y={height - pad + 4} fontSize={9} fill={C.textSub}>{min.toFixed(1)}</text>
      <text x={4} y={pad + 4} fontSize={9} fill={C.textSub}>{max.toFixed(1)}</text>
    </svg>
  );
}

// ── Main InferenceTab ──────────────────────────────────────────────────────────
export default function InferenceTab() {
  // Location
  const [lat, setLat] = useState("51.2194");
  const [lon, setLon] = useState("4.4025");
  const [scanDuration, setScanDuration] = useState(120);
  const [collectHours, setCollectHours] = useState(24);

  // Scan state
  const [scanId,    setScanId]    = useState(null);
  const [scanData,  setScanData]  = useState(null);
  const [scanning,  setScanning]  = useState(false);
  const scanPoll = useRef(null);

  // Station selection
  const [selected, setSelected] = useState(null); // chosen station

  // Activation state
  const [activating,    setActivating]    = useState(false);
  const [activated,     setActivated]     = useState(null); // activation response
  const [forecastStart, setForecastStart] = useState(null); // blob URL
  const [forecastEnd,   setForecastEnd]   = useState(null); // blob URL
  const [fetchingForecastEnd, setFetchingForecastEnd] = useState(false); // spinner while the completion-time forecast fetch is in flight

  // Collection progress
  const [progress,     setProgress]     = useState(null);
  const [collectionDone, setCollectionDone] = useState(false);
  const progressPoll = useRef(null);

  // Mapping
  const [sensorFields,   setSensorFields]   = useState([]);
  const [sensorMap,      setSensorMap]      = useState({});
  const [sensorExcluded, setSensorExcluded] = useState({});
  const [forecastFields,   setForecastFields]   = useState(Object.keys(FORECAST_DEFAULTS));
  const [forecastMap,      setForecastMap]      = useState({ ...FORECAST_DEFAULTS });
  const [forecastExcluded, setForecastExcluded] = useState({});

  // CSV upload
  const [csvFileText,   setCsvFileText]   = useState(null); // full raw text of uploaded file
  const [csvSep,        setCsvSep]        = useState(",");
  const [csvPreviewRows, setCsvPreviewRows] = useState([]); // first few rows, raw string[][]
  const [csvHasHeader,  setCsvHasHeader]  = useState(true); // is row 0 the column names?
  const [csvHeaders,    setCsvHeaders]    = useState([]);   // editable column names
  const [csvColumnsConfirmed, setCsvColumnsConfirmed] = useState(false);
  const [csvFields,   setCsvFields]   = useState([]);
  const [csvData,     setCsvData]     = useState(null);
  const [csvMap,      setCsvMap]      = useState({});
  const [csvExcluded, setCsvExcluded] = useState({});

  // Downloading
  const [downloading, setDownloading] = useState("");
  const [error, setError] = useState("");

  // Inference
  const [weightsMode,     setWeightsMode]     = useState("with_forecast"); // with_forecast | without_forecast
  const [weightSets,      setWeightSets]      = useState([]); // fetched from the local inference server
  const [weightsLoading,  setWeightsLoading]  = useState(true);
  const [weightsError,    setWeightsError]    = useState("");
  const [contextWindow, setContextWindow] = useState(null); // minutes, picked first — narrows weight sets
  const [weightsId,     setWeightsId]     = useState(null);
  const [inferring,       setInferring]       = useState(false);
  const [inferenceResult, setInferenceResult] = useState(null); // { data, timestamp }

  // Preview (head of sensor + merged forecast context, before running the model)
  const [previewing,  setPreviewing]  = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // Kept separate from the shared `error` state above so a stale "still
  // collecting" message in the Data Preview card never bleeds into other
  // cards (export, inference, etc.) that share `error`.
  const [previewError, setPreviewError] = useState("");

  // Postprocessing dashboard — which channel is currently plotted
  const [dashboardChannel, setDashboardChannel] = useState(null);

  // Recurring inference — now a persistent SERVER-SIDE schedule (see
  // inference_runner.service), not a browser setTimeout loop. Defaults match
  // the requested "24h collection window, hourly runs" — both overridable
  // per schedule below. This card only creates/lists/stops the schedule
  // config; inference_runner.py is what actually executes it and writes to
  // inference_predictions. See the Results tab to browse run history —
  // that history now survives closing this tab, unlike before.
  const [scheduleFrequencyMinutes, setScheduleFrequencyMinutes] = useState(60);
  const [scheduleCollectHours,     setScheduleCollectHours]     = useState(24);
  const [schedules,      setSchedules]      = useState([]);
  const [schedulesError, setSchedulesError] = useState("");
  const [creatingSchedule, setCreatingSchedule] = useState(false);

  async function loadSchedules() {
    try {
      const r = await inferenceApiJson("/schedules");
      setSchedules(r.schedules || []);
    } catch (e) {
      setSchedulesError(e.message);
    }
  }

  useEffect(() => {
    if (activated) loadSchedules();
  }, [activated]);

  // Moved up from further down in this file — was previously declared
  // ~500 lines below its first use at scheduleForThisDevice just below,
  // which is a temporal-dead-zone violation (referencing a const before
  // its own declaration line executes). Falls back to wr_device_id for
  // stations activated via the existing weather_readings bridge (poller
  // is null in that response shape) — see fetchSensorBlob() for the same
  // fallback pattern.
  const device_id = activated?.wr_device_id ?? activated?.poller?.device_id;

  const scheduleForThisDevice = schedules.find(s => s.device_id === device_id && s.active);

  async function createSchedule() {
    if (!device_id || !selected || !weightsId) {
      setSchedulesError("Activate a station and pick a weight set before scheduling.");
      return;
    }
    setCreatingSchedule(true); setSchedulesError("");
    try {
      const sensorMapping = Object.entries(sensorMap)
        .filter(([f]) => !sensorExcluded[f] && sensorMap[f]?.to)
        .map(([from, m]) => ({ from_field: from, to_channel: m.to, source_unit: m.source_unit || "" }));

      await inferenceApiJson("/schedules", {
        method: "POST",
        body: JSON.stringify({
          device_id,
          source: selected.source,
          station_id: selected.station_id,
          lat: selected.lat,
          lon: selected.lon,
          location_name: selected.station_name || "",
          weights_id: weightsId,
          weights_mode: weightsMode,
          collect_hours: scheduleCollectHours,
          frequency_minutes: scheduleFrequencyMinutes,
          sensor_mapping: sensorMapping,
        }),
      });
      await loadSchedules();
    } catch (e) {
      setSchedulesError(e.message);
    } finally {
      setCreatingSchedule(false);
    }
  }

  async function stopScheduleBackend(id) {
    setSchedulesError("");
    try {
      await inferenceApi(`/schedules/${id}`, { method: "DELETE" });
      await loadSchedules();
    } catch (e) {
      setSchedulesError(e.message);
    }
  }

  // Auto-load the data preview as soon as there's something to preview, and
  // keep it fresh as the picture changes (forecast_end arriving, a weight
  // set being picked). No click required — "Refresh preview" below is only
  // for re-checking after you tweak the channel mapping.
  const previewSignature = `${sensorFields.length > 0}|${!!forecastStart}|${!!forecastEnd}|${weightsId || ""}`;
  useEffect(() => {
    if (sensorFields.length > 0 && forecastStart) {
      runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSignature]);

  // Auto-retry the preview while it's specifically blocked on "not enough
  // continuous history yet" — this resolves itself over time (the poller
  // keeps collecting in the background), so keep checking every 20s rather
  // than making the person keep clicking "Refresh preview" themselves.
  useEffect(() => {
    if (!previewError) return;
    const id = setInterval(() => {
      if (!previewing) runPreview();
    }, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewError]);

  // Fetch the real list of available weight sets from the local inference
  // server — filtered by weightsMode, and sorted/tagged by distance to the
  // current lat/lon (same coordinates used for scanning) so the nearest
  // trained location is easy to spot. Re-runs whenever the mode or
  // location changes, not just on mount.
  useEffect(() => {
    (async () => {
      setWeightsLoading(true); setWeightsError("");
      try {
        const params = new URLSearchParams({ mode: weightsMode, lat, lon });
        const r = await fetch(`${INFERENCE_API_BASE}/weights?${params}`);
        if (!r.ok) throw new Error(`inference server returned ${r.status}`);
        const d = await r.json();
        setWeightSets(d.weights || []);
        setContextWindow(null); setWeightsId(null); // reset selection — different mode/location may have different context windows available
      } catch (e) {
        setWeightsError(`Couldn't reach the inference server at ${INFERENCE_API_BASE}: ${e.message}`);
      } finally {
        setWeightsLoading(false);
      }
    })();
  }, [weightsMode, lat, lon]);

  // ── Scan ──────────────────────────────────────────────────────────────────────
  async function startScan() {
    setScanning(true); setScanData(null); setScanId(null);
    setSelected(null); setActivated(null); setCollectionDone(false);
    setProgress(null); setError("");

    try {
      const d = await apiJson("/inference/scan/start", {
        method: "POST",
        body: JSON.stringify({
          lat: +lat, lon: +lon,
          radius_km: 150,
          scan_duration_s: scanDuration,
        }),
      });
      setScanId(d.scan_id);
      // Poll every 4s
      scanPoll.current = setInterval(async () => {
        const sd = await apiJson(`/inference/scan/${d.scan_id}`);
        setScanData(sd);
        if (sd.recommended && !selected) setSelected(sd.recommended);
        if (sd.status === "complete") {
          clearInterval(scanPoll.current);
          setScanning(false);
        }
      }, 4000);
    } catch (e) {
      setError(e.message);
      setScanning(false);
    }
  }

  useEffect(() => () => {
    clearInterval(scanPoll.current);
    clearInterval(progressPoll.current);
  }, []);

  // ── Activate ──────────────────────────────────────────────────────────────────
  async function activate() {
    if (!selected) return;
    setActivating(true); setError(""); setCollectionDone(false);

    try {
      // Fetch forecast #1 immediately
      const fStartBlob = await fetchForecastBlob("start");
      setForecastStart(fStartBlob);

      // Activate poller + bridge
      const d = await apiJson("/inference/activate", {
        method: "POST",
        body: JSON.stringify({
          source:           selected.source,
          station_id:       selected.station_id,
          lat:              selected.lat,
          lon:              selected.lon,
          station_name:     selected.station_name,
          interval_seconds: 60,
          collect_hours:    collectHours,
        }),
      });
      setActivated(d);

      // Start progress polling
      startProgressPoll(d.poller?.device_id || `${selected.source.toUpperCase()}_${selected.station_id}`);

    } catch (e) {
      setError(e.message);
    } finally {
      setActivating(false);
    }
  }

  async function fetchForecastBlob(label) {
    const mapping = Object.entries(forecastMap)
      .filter(([f]) => !forecastExcluded[f] && forecastMap[f]?.to)
      .map(([from, m]) => ({ from_field: from, to_channel: m.to, source_unit: m.source_unit || "" }));

    const r = await inferenceApi("/forecast/export", {
      method: "POST",
      body: JSON.stringify({ lat: +lat, lon: +lon, days: 7, mapping }),
    });
    const blob = await r.blob();
    return { blob, url: URL.createObjectURL(blob), label, timestamp: ts() };
  }

  // ── Progress polling ──────────────────────────────────────────────────────────
  function startProgressPoll(device_id) {
    const minPoints = calcMinPoints(selected?.source, collectHours);
    const targetSpanMinutes = collectHours * 60;

    progressPoll.current = setInterval(async () => {
      try {
        const d = await apiJson(`/inference/data?device_id=${device_id}&hours=${collectHours + 2}`);
        const info = d.data?.[0];
        if (info) {
          // Raw point count (minPoints) assumes perfectly even reporting
          // intervals — real stations don't land on exact clock boundaries,
          // so hitting minPoints doesn't guarantee the CONTINUOUS span the
          // model actually needs. earliest/latest (already returned by this
          // endpoint) let us check the real thing: how much unbroken time
          // the collected data actually spans.
          const spanMinutes = (info.earliest && info.latest)
            ? (new Date(info.latest) - new Date(info.earliest)) / 60000
            : 0;

          setProgress({
            ...info,
            min_points: minPoints,
            span_minutes: spanMinutes,
            target_span_minutes: targetSpanMinutes,
          });

          // Load sensor field mapping from discovered fields
          if (info.count > 0 && sensorFields.length === 0) {
            loadSensorFields(device_id);
          }

          // Ready only once BOTH the point count and the actual continuous
          // span meet target — span is the one that matters for the model;
          // count is kept as a sanity floor. Once span crosses the target it
          // only grows from here (the bridged earliest timestamp never
          // moves, the poller only appends forward), so this can't flap
          // back to "not ready" later.
          const spanReady = spanMinutes >= targetSpanMinutes - 1; // 1-min rounding tolerance
          if (info.count >= minPoints && spanReady && !collectionDone) {
            setCollectionDone(true);
            clearInterval(progressPoll.current);
            // Fetch forecast #2
            setFetchingForecastEnd(true);
            try {
              const fEndBlob = await fetchForecastBlob("end");
              setForecastEnd(fEndBlob);
            } finally {
              setFetchingForecastEnd(false);
            }
          }
        }
      } catch {}
    }, 30000); // every 30s
  }

  function calcMinPoints(source, hours) {
    const INTERVAL = { nea: 1, nws: 5, rmi: 10, dwd: 10, knmi: 10,
                       meteoswiss_live: 10, imgw: 60, metar: 30, wis2: 20 };
    const interval = INTERVAL[source] || 10;
    return Math.max(4, (hours * 60) / interval);
  }

  async function loadSensorFields(device_id) {
    try {
      // Get suggestions from the inference wrap
      const r = await inferenceApiJson("/export", {
        method: "POST",
        body: JSON.stringify({ device_id, hours: collectHours + 2, mapping: [] }),
      });
      if (r.fields) {
        const fields = r.fields.map(f => f.field_name).filter(f =>
          !["id","device_id","source","station_name","lat","lon",
            "collected_at","interval_seconds","mqtt_topic"].includes(f)
        );
        setSensorFields(fields);
        // Auto-map known fields
        const autoMap = {};
        const autoEx  = {};
        fields.forEach(f => {
          if (SENSOR_DEFAULTS[f]) {
            autoMap[f] = { ...SENSOR_DEFAULTS[f] };
          } else {
            autoMap[f] = { to: "", source_unit: "" };
            autoEx[f]  = true; // auto-exclude unknown fields
          }
        });
        setSensorMap(autoMap);
        setSensorExcluded(autoEx);
      }
    } catch {}
  }

  // ── CSV upload ────────────────────────────────────────────────────────────────
  const splitRow = (line, sep) => line.split(sep).map(v => v.trim().replace(/^"|"$/g, ""));

  function handleCsvUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.trim().split("\n");
      const sep   = lines[0].includes(",") ? "," : lines[0].includes(";") ? ";" : "\t";
      const preview = lines.slice(0, 6).map(l => splitRow(l, sep));
      const width = preview[0]?.length || 0;

      // Reset any previous confirmed state — user re-uploaded a new file
      setCsvColumnsConfirmed(false);
      setCsvFields([]); setCsvData(null); setCsvMap({}); setCsvExcluded({});

      setCsvFileText(text);
      setCsvSep(sep);
      setCsvPreviewRows(preview);
      setCsvHasHeader(true);
      // Default guess: use row 0 as header names
      setCsvHeaders(preview[0] || Array.from({ length: width }, (_, i) => `Column${i + 1}`));
    };
    reader.readAsText(file);
  }

  // Regenerate default header labels when the "first row is header" toggle flips
  function toggleHasHeader(next) {
    setCsvHasHeader(next);
    const width = csvPreviewRows[0]?.length || csvHeaders.length;
    if (next) {
      setCsvHeaders(csvPreviewRows[0] || csvHeaders);
    } else {
      setCsvHeaders(Array.from({ length: width }, (_, i) => `Column${i + 1}`));
    }
  }

  function renameCsvHeader(i, value) {
    setCsvHeaders(hs => hs.map((h, idx) => (idx === i ? value : h)));
  }

  // Parse the full file using the confirmed headers + header toggle, and
  // auto-suggest a mapping (still requires the user to confirm/complete it below).
  function confirmCsvColumns() {
    if (!csvFileText) return;
    const lines = csvFileText.trim().split("\n");
    const rawRows = lines.map(l => splitRow(l, csvSep));
    const dataRows = csvHasHeader ? rawRows.slice(1) : rawRows;
    const headers = csvHeaders.map((h, i) => h?.trim() || `Column${i + 1}`);
    const rows = dataRows.map(r => Object.fromEntries(headers.map((h, i) => [h, r[i]])));

    setCsvFields(headers);
    setCsvData(rows);

    const autoMap = {};
    const autoEx  = {};
    headers.forEach(h => {
      autoMap[h] = SENSOR_DEFAULTS[h] || { to: "", source_unit: "" };
      if (!SENSOR_DEFAULTS[h]) autoEx[h] = true; // unknown fields start excluded, not silently mapped
    });
    setCsvMap(autoMap);
    setCsvExcluded(autoEx);
    setCsvColumnsConfirmed(true);
  }

  // ── Export ────────────────────────────────────────────────────────────────────
  async function fetchSensorBlob() {
    const device_id = activated.wr_device_id ?? activated.poller?.device_id;
    const mapping   = Object.entries(sensorMap)
      .filter(([f]) => !sensorExcluded[f] && sensorMap[f]?.to)
      .map(([from, m]) => ({ from_field: from, to_channel: m.to, source_unit: m.source_unit || "" }));

    const r = await inferenceApi("/export", {
      method: "POST",
      body: JSON.stringify({ device_id, hours: collectHours + 2, mapping }),
    });
    return await r.blob();
  }

  async function exportSensor() {
    if (!activated) return;
    const pending = unmappedFields(sensorFields, sensorMap, sensorExcluded);
    if (pending.length > 0) {
      setError(`Map or exclude every sensor field first: ${pending.join(", ")}`);
      return;
    }
    setDownloading("sensor"); setError("");
    try {
      const blob = await fetchSensorBlob();
      const fname = `inference_sensor_${selected.source}_${selected.station_id}_${collectHours}h_${ts()}.parquet`;
      triggerDownload(blob, fname);
    } catch (e) { setError(e.message); }
    finally { setDownloading(""); }
  }

  async function exportCsv() {
    if (!csvData) return;
    const pending = unmappedFields(csvFields, csvMap, csvExcluded);
    if (pending.length > 0) {
      setError(`Map or exclude every column first: ${pending.join(", ")}`);
      return;
    }
    setDownloading("csv"); setError("");
    try {
      // Build parquet from CSV via API pipeline
      // For now trigger direct download as CSV — parquet conversion needs backend
      const mapping = Object.entries(csvMap)
        .filter(([f]) => !csvExcluded[f] && csvMap[f]?.to)
        .map(([from, m]) => ({ from_field: from, to_channel: m.to, source_unit: m.source_unit || "" }));
      // Convert locally to JSON and pass to export endpoint
      alert("CSV parquet export — coming soon. Mapping: " + JSON.stringify(mapping.slice(0,2)));
    } catch (e) { setError(e.message); }
    finally { setDownloading(""); }
  }

  function triggerDownload(blob, filename) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ── Inference ─────────────────────────────────────────────────────────────────
  // Shared guard checks used by both preview and real inference — returns
  // the chosen weight set, or null (having already set `error`) if not ready.
  function checkReadyForServer() {
    if (!activated) { setError("Activate a station and collect data before running inference."); return null; }
    if (!forecastStart || !forecastEnd) {
      setError("Both forecasts (fetched at activation and at collection end) must be ready first.");
      return null;
    }
    const chosen = weightSets.find(w => w.id === weightsId);
    if (!chosen) { setError("Pick a context window and weight set first."); return null; }

    if (collectHours * 60 < chosen.context_window) {
      const neededHours = Math.ceil(chosen.context_window / 60);
      setError(
        `This weight set needs ${neededHours}h (${chosen.context_window} min) of collected data, ` +
        `but Step 1 only collected ${collectHours}h. Pick a smaller context window, or re-run ` +
        `Step 1 with "Collect for" set to at least ${neededHours}h.`
      );
      return null;
    }

    const pendingSensor = unmappedFields(sensorFields, sensorMap, sensorExcluded);
    if (pendingSensor.length > 0) {
      setError("Finish the sensor channel mapping (map or exclude every field) before running inference.");
      return null;
    }
    return chosen;
  }

  async function buildInferenceForm(weightsIdChosen) {
    const blob = await fetchSensorBlob();
    const form = new FormData();
    form.append("sensor_parquet", blob, "sensor.parquet");
    form.append("forecast_start_parquet", forecastStart.blob, "forecast_start.parquet");
    form.append("forecast_end_parquet", forecastEnd.blob, "forecast_end.parquet");
    form.append("weights_id", weightsIdChosen);
    form.append("weights_mode", weightsMode);
    form.append("lat", lat);
    form.append("lon", lon);
    if (device_id) form.append("device_id", device_id);
    return form;
  }

  async function runPreview() {
    if (!activated) { setError("Activate a station before previewing."); return; }
    if (!forecastStart) { setError("Forecast hasn't been fetched yet — wait for activation to finish."); return; }
    const pendingSensor = unmappedFields(sensorFields, sensorMap, sensorExcluded);
    if (pendingSensor.length > 0) {
      setError("Finish the sensor channel mapping (map or exclude every field) before previewing.");
      return;
    }

    setPreviewing(true); setPreviewError(""); setPreviewData(null);
    try {
      const blob = await fetchSensorBlob();
      const form = new FormData();
      form.append("sensor_parquet", blob, "sensor.parquet");
      form.append("forecast_start_parquet", forecastStart.blob, "forecast_start.parquet");
      const forecastEndBlob = forecastEnd ? forecastEnd.blob : forecastStart.blob;
      form.append("forecast_end_parquet", forecastEndBlob, "forecast_end.parquet");
      if (weightsId) {
        form.append("weights_id", weightsId);
        form.append("weights_mode", weightsMode);
        form.append("lat", lat);
        form.append("lon", lon);
      }

      const r = await fetch(`${INFERENCE_API_BASE}/preview`, { method: "POST", body: form });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || r.statusText);
      }
      setPreviewData(await r.json());
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function runInference() {
    const chosen = checkReadyForServer();
    if (!chosen) return;

    setInferring(true); setError("");
    try {
      const form = await buildInferenceForm(chosen.id);
      const r = await fetch(`${INFERENCE_API_BASE}/predict`, { method: "POST", body: form });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || r.statusText);
      }
      const d = await r.json();
      // Seed the dashboard's channel picker with a channel that's actually
      // comparable against a baseline (present in both d.channels and
      // d.baseline_forecast.channels), rather than just d.channels[0] —
      // inference_server.py's CHANNELS now puts the sensor-side channels
      // first, tagged "<name>__sensor", and those have no NWP baseline to
      // plot against. Same reasoning as the comparableChannels filter used
      // below in the postprocessing dashboard.
      const baselineChannelsForSeed = d.baseline_forecast?.channels || [];
      const comparableForSeed = (d.channels || []).filter(c => baselineChannelsForSeed.includes(c));
      setDashboardChannel(comparableForSeed[0] || d.channels?.[0] || null);
      setInferenceResult({ data: d, timestamp: ts() });
    } catch (e) {
      setError(e.message);
    } finally {
      setInferring(false);
    }
  }

  function downloadForecast(fc) {
    if (!fc) return;
    triggerDownload(fc.blob, `inference_forecast_${fc.label}_${+lat}_${+lon}_${fc.timestamp}.parquet`);
  }

  // ── Derived state ─────────────────────────────────────────────────────────────
  // device_id declared earlier (near scheduleForThisDevice) — see that comment.
  const minPoints   = selected ? calcMinPoints(selected.source, collectHours) : 24;
  const curCount    = progress?.count || (activated?.bridge_rows || 0);
  const completePct = Math.min(Math.round((curCount / minPoints) * 100), 100);
  const spanMinutes = progress?.span_minutes || 0;
  const targetSpanMinutes = collectHours * 60;
  const spanPct = Math.min(Math.round((spanMinutes / targetSpanMinutes) * 100), 100);
  const isReady     = collectionDone || (activated?.ready_now && curCount >= minPoints && spanMinutes >= targetSpanMinutes - 1);
  const contextWindows = [...new Set(weightSets.map(w => w.context_window))].sort((a, b) => a - b);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "Inter, sans-serif", color: C.text }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        input, select { background: ${C.surfaceAlt}; border: 1px solid ${C.border}; color: ${C.text}; border-radius: 6px; padding: 7px 10px; font-size: 13px; font-family: inherit; outline: none; }
        input:focus, select:focus { border-color: ${C.accent}; }
        button { background: ${C.surfaceAlt}; border: 1px solid ${C.border}; color: ${C.text}; border-radius: 6px; padding: 8px 14px; font-size: 13px; font-family: inherit; cursor: pointer; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; }
        button:hover:not(:disabled) { border-color: ${C.accent}; color: ${C.accent}; }
        button.primary { background: ${C.accent}; border-color: ${C.accent}; color: #fff; }
        button.primary:hover:not(:disabled) { opacity: 0.88; color: #fff; }
        button.success { background: ${C.success}; border-color: ${C.success}; color: #fff; }
        button:disabled { opacity: 0.4; cursor: not-allowed; }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 6px 8px; }
        th { font-size: 11px; font-weight: 500; color: ${C.textSub}; border-bottom: 1px solid ${C.border}; }
        td { border-bottom: 1px solid ${C.border}; font-size: 12px; }
      `}</style>

      {/* ── SECTION 1: Location & Scan ── */}
      <Card style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          📡 Step 1 — Discover live stations
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div><Label>Latitude</Label><input value={lat} onChange={e => setLat(e.target.value)} style={{ width: "100%" }} /></div>
          <div><Label>Longitude</Label><input value={lon} onChange={e => setLon(e.target.value)} style={{ width: "100%" }} /></div>
          <div>
            <Label>Scan duration (s)</Label>
            <select value={scanDuration} onChange={e => setScanDuration(+e.target.value)} style={{ width: "100%" }}>
              <option value={60}>1 min</option>
              <option value={120}>2 min</option>
              <option value={180}>3 min</option>
              <option value={300}>5 min</option>
            </select>
          </div>
          <div>
            <Label>Collect for (hours)</Label>
            <select value={collectHours} onChange={e => setCollectHours(+e.target.value)} style={{ width: "100%" }}>
              {[1,2,3,4,6,8,12,24].map(h => <option key={h} value={h}>{h}h</option>)}
            </select>
          </div>
          <button className="primary" onClick={startScan} disabled={scanning}>
            {scanning ? <><Spinner /> Scanning…</> : "🔍 Scan"}
          </button>
        </div>

        {scanning && (
          <div style={{ marginTop: 12, fontSize: 12, color: C.textSub }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ animation: "pulse 1.5s infinite", color: C.accent }}>●</span>
              <span>
                {scanData?.phase === "wis2"
                  ? `WIS2 streaming... ${scanData?.found || 0} stations found so far`
                  : "Checking live sources..."}
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* ── SECTION 2: Station results ── */}
      {scanData?.results?.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <p style={{ fontSize: 14, fontWeight: 600 }}>
              📊 Available stations
              <span style={{ fontSize: 12, fontWeight: 400, color: C.textSub, marginLeft: 8 }}>
                {scanData.results.length} found
                {scanData.status === "scanning" && " (still scanning WIS2...)"}
                {scanData.status === "complete" && " ✓"}
              </span>
            </p>
          </div>

          {scanData.recommended && (
            <div style={{
              background: C.accentDim, border: `1px solid ${C.accent}`,
              borderRadius: 8, padding: "10px 14px", marginBottom: 12,
              display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <div>
                <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>RECOMMENDED</span>
                <p style={{ fontSize: 13, fontWeight: 600, margin: "2px 0" }}>
                  {scanData.recommended.station_name}
                </p>
                <p style={{ fontSize: 11, color: C.textSub }}>
                  {scanData.recommended.source.toUpperCase()} ·{" "}
                  {scanData.recommended.distance_km?.toFixed(1)}km ·{" "}
                  {scanData.recommended.frequency} ·{" "}
                  {scanData.recommended.sample_temp != null ? `${scanData.recommended.sample_temp.toFixed(1)}°C` : ""}
                </p>
              </div>
              <button
                className={selected?.station_id === scanData.recommended.station_id ? "success" : "primary"}
                onClick={() => setSelected(scanData.recommended)}
              >
                {selected?.station_id === scanData.recommended.station_id ? "✓ Selected" : "Select"}
              </button>
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Source</th><th>Station</th><th>Distance</th>
                  <th>Frequency</th><th>Temp</th><th>Wind</th><th>Live</th><th></th>
                </tr>
              </thead>
              <tbody>
                {scanData.results.map((s, i) => {
                  const isSelected = selected?.station_id === s.station_id && selected?.source === s.source;
                  return (
                    <tr key={i} style={{ background: isSelected ? C.accentDim : "transparent" }}>
                      <td>{pill(s.source.toUpperCase())}</td>
                      <td style={{ fontWeight: 500 }}>
                        {s.station_name?.slice(0, 25)}
                        {s.source === "wis2" && <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 4 }}>stream</span>}
                      </td>
                      <td style={{ fontFamily: "monospace" }}>{s.distance_km?.toFixed(1)} km</td>
                      <td style={{ color: freqColor(s.frequency), fontWeight: 500 }}>{s.frequency}</td>
                      <td style={{ fontFamily: "monospace" }}>
                        {s.sample_temp != null ? `${s.sample_temp.toFixed(1)}°C` : "—"}
                      </td>
                      <td>{s.has_wind ? <span style={{ color: C.success }}>✓</span> : <span style={{ color: C.textMuted }}>—</span>}</td>
                      <td>
                        {s.live_test === "ok"
                          ? <span style={{ color: C.success }}>● live</span>
                          : <span style={{ color: C.textMuted }}>○ {s.live_test}</span>}
                      </td>
                      <td>
                        <button
                          onClick={() => setSelected(s)}
                          style={{
                            fontSize: 11, padding: "3px 10px",
                            ...(isSelected ? { background: C.success, borderColor: C.success, color: "#fff" } : {})
                          }}
                        >
                          {isSelected ? "✓" : "Select"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── SECTION 3: CSV Upload (alternative to station) ── */}
      <Card style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
          📂 Or upload your own data (CSV / TXT)
        </p>
        <p style={{ fontSize: 12, color: C.textSub, marginBottom: 10 }}>
          If you already have sensor data, upload it here and map columns to ML channels.
        </p>
        <input type="file" accept=".csv,.txt,.tsv"
          onChange={handleCsvUpload}
          style={{ fontSize: 12, color: C.textSub }} />

        {csvPreviewRows.length > 0 && !csvColumnsConfirmed && (
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textSub, marginBottom: 10 }}>
              <input type="checkbox" checked={csvHasHeader}
                onChange={e => toggleHasHeader(e.target.checked)}
                style={{ cursor: "pointer" }} />
              First row contains the column names
            </label>

            <p style={{ fontSize: 11, color: C.textSub, marginBottom: 6 }}>
              Preview — check this actually looks like a header row, or type your own names below:
            </p>
            <div style={{ overflowX: "auto", marginBottom: 10 }}>
              <table style={{ fontSize: 11 }}>
                <tbody>
                  {csvPreviewRows.map((row, ri) => (
                    <tr key={ri} style={{ opacity: csvHasHeader && ri === 0 ? 1 : 0.75 }}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={{
                          padding: "4px 8px", whiteSpace: "nowrap",
                          fontWeight: csvHasHeader && ri === 0 ? 600 : 400,
                          color: csvHasHeader && ri === 0 ? C.accent : C.text,
                        }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p style={{ fontSize: 11, color: C.textSub, marginBottom: 6 }}>Column names to use:</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {csvHeaders.map((h, i) => (
                <input key={i} value={h} onChange={e => renameCsvHeader(i, e.target.value)}
                  style={{ fontSize: 11, width: 120 }} />
              ))}
            </div>

            <button className="primary" onClick={confirmCsvColumns}>
              Confirm columns →
            </button>
          </div>
        )}

        {csvColumnsConfirmed && csvData && (
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <p style={{ fontSize: 12, color: C.success }}>
              ✓ Loaded {csvData.length} rows, {csvFields.length} columns
            </p>
            <button
              onClick={() => { setCsvColumnsConfirmed(false); }}
              style={{ fontSize: 11, padding: "3px 10px", color: C.textSub }}
            >
              Edit columns
            </button>
          </div>
        )}
      </Card>

      {/* ── SECTION 4: Activate ── */}
      {(selected || csvData) && !activated && (
        <Card style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
            ⚡ Step 2 — Activate collection
          </p>
          {selected && (
            <div style={{ background: C.surfaceAlt, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{selected.station_name}</span>
              <span style={{ color: C.textSub, marginLeft: 8 }}>
                {selected.source.toUpperCase()} · {selected.distance_km?.toFixed(1)}km · {selected.frequency}
              </span>
            </div>
          )}
          <p style={{ fontSize: 12, color: C.textSub, marginBottom: 12 }}>
            On activate: fetches forecast immediately, bridges last 4h historical data,
            then polls every minute for {collectHours}h. Needs ~{calcMinPoints(selected?.source, collectHours)} data points.
          </p>
          <Err msg={error} />
          <button className="primary" onClick={activate} disabled={activating} style={{ width: "100%" }}>
            {activating
              ? <><Spinner /> Activating — fetching forecast + historical data…</>
              : `🚀 Activate & start ${collectHours}h collection`}
          </button>
        </Card>
      )}

      {/* ── SECTION 5: Progress ── */}
      {activated && (
        <Card style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
            {isReady ? "✅ Collection complete" : "⏳ Collecting data..."}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
            {[
              { label: "Rows collected", value: curCount },
              { label: "Target", value: minPoints },
              { label: "Completeness", value: `${completePct}%` },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: C.surfaceAlt, borderRadius: 8, padding: "10px 12px" }}>
                <p style={{ fontSize: 10, color: C.textSub, marginBottom: 4 }}>{label}</p>
                <p style={{ fontSize: 20, fontWeight: 600, color: isReady ? C.success : C.accent }}>{value}</p>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 11, color: C.textSub, marginBottom: 2 }}>Row count (assumes even reporting intervals)</p>
          <ProgressBar pct={completePct} color={isReady ? C.success : C.accent} />

          <p style={{ fontSize: 11, color: C.textSub, marginTop: 10, marginBottom: 2 }}>
            Continuous coverage — {Math.round(spanMinutes)}/{targetSpanMinutes} min (what the model actually needs)
          </p>
          <ProgressBar pct={spanPct} color={spanPct >= 100 ? C.success : C.warn} />

          <div style={{ display: "flex", gap: 8, marginTop: 10, fontSize: 12, color: C.textSub, flexWrap: "wrap" }}>
            <span>Station: <strong style={{ color: C.text }}>{activated.poller?.station_name}</strong></span>
            <span>·</span>
            <span>Bridge loaded: <strong style={{ color: C.success }}>{activated.bridge_rows} rows</strong></span>
            <span>·</span>
            <span>Device: <code style={{ color: C.accent, fontSize: 11 }}>{device_id}</code></span>
            {progress?.latest && (
              <>
                <span>·</span>
                <span>Last: {progress.latest.slice(0, 16).replace("T", " ")}</span>
              </>
            )}
          </div>

          {!isReady && (
            <p style={{ fontSize: 11, color: C.textSub, marginTop: 8 }}>
              Auto-refreshes every 30s. Estimated time: ~{Math.ceil((minPoints - curCount) * (selected ? { nea:1,nws:5,rmi:10,dwd:10,knmi:10,metar:30,wis2:20 }[selected.source] || 10 : 10))} min
              {spanMinutes > 0 && spanMinutes < targetSpanMinutes && (
                <> — continuous coverage still needs ~{Math.ceil(targetSpanMinutes - spanMinutes)} more minutes.</>
              )}
            </p>
          )}

          {fetchingForecastEnd && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 12, color: C.textSub }}>
              <Spinner /> Fetching the completion-time forecast — this can take a few seconds…
            </div>
          )}

          {isReady && forecastEnd && !fetchingForecastEnd && (
            <div style={{
              background: C.successDim, border: `1px solid ${C.success}`,
              borderRadius: 8, padding: "10px 14px", marginTop: 12, fontSize: 12
            }}>
              ✅ {collectHours}h collection complete — forecast #2 fetched and ready for download
            </div>
          )}
        </Card>
      )}

      {/* ── SECTION 6: Forecast downloads ── */}
      {(forecastStart || forecastEnd || fetchingForecastEnd) && (
        <Card style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>🌤 Forecasts</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {forecastStart && (
              <button onClick={() => downloadForecast(forecastStart)} style={{ flex: 1 }}>
                ↓ forecast_start_{forecastStart.timestamp}.parquet
                <span style={{ fontSize: 10, color: C.textSub, marginLeft: 4 }}>(at activation)</span>
              </button>
            )}
            {forecastEnd ? (
              <button className="success" onClick={() => downloadForecast(forecastEnd)} style={{ flex: 1 }}>
                ↓ forecast_end_{forecastEnd.timestamp}.parquet
                <span style={{ fontSize: 10, marginLeft: 4 }}>(at completion)</span>
              </button>
            ) : fetchingForecastEnd ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12, color: C.textSub }}>
                <Spinner /> Fetching completion forecast…
              </div>
            ) : null}
          </div>
        </Card>
      )}

      {/* ── SECTION 7: Channel mapping ── */}
      {(isReady || csvData) && (
        <Card style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>🔀 Channel mapping</p>

          {sensorFields.length > 0 && (
            <MappingTable
              title="Sensor data"
              fields={sensorFields}
              mapping={sensorMap}
              setMapping={setSensorMap}
              excluded={sensorExcluded}
              setExcluded={setSensorExcluded}
            />
          )}

          {csvFields.length > 0 && (
            <MappingTable
              title="Uploaded CSV data"
              fields={csvFields}
              mapping={csvMap}
              setMapping={setCsvMap}
              excluded={csvExcluded}
              setExcluded={setCsvExcluded}
            />
          )}

          <MappingTable
            title="Forecast data (Open-Meteo)"
            fields={forecastFields}
            mapping={forecastMap}
            setMapping={setForecastMap}
            excluded={forecastExcluded}
            setExcluded={setForecastExcluded}
          />
        </Card>
      )}

      {/* ── SECTION 7.5: Data preview ── */}
      {sensorFields.length > 0 && forecastStart && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
            <p style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              👀 Data preview
              {previewing && <Spinner />}
            </p>
            <button onClick={runPreview} disabled={previewing}>
              {previewing ? <><Spinner /> Loading…</> : "Refresh preview"}
            </button>
          </div>
          <p style={{ fontSize: 12, color: C.textSub, marginBottom: 12 }}>
            {weightsId
              ? "Showing the exact context-window slice the model would be fed."
              : "Showing the most recent mapped rows collected so far — pick a weight set below for the exact model-input slice."}
            {" "}{!forecastEnd && "(forecast_end hasn't been fetched yet — using forecast_start only, for now.)"}
          </p>

          {previewError ? (
            (() => {
              const shortfall = parseContinuityShortfall(previewError);
              if (shortfall) {
                const pct = Math.min(Math.round((shortfall.have / shortfall.need) * 100), 100);
                return (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <Spinner />
                      <p style={{ fontSize: 12, color: C.textSub }}>
                        Still building enough continuous history for this weight set —{" "}
                        <strong style={{ color: C.text }}>{shortfall.have}/{shortfall.need} minutes</strong> collected so far.
                        This resolves automatically as the poller keeps collecting (rechecking every 20s) — no action needed.
                      </p>
                    </div>
                    <ProgressBar pct={pct} color={C.accent} />
                  </div>
                );
              }
              const leadingGap = parseLeadingGapError(previewError);
              if (leadingGap) {
                return (
                  <div style={{
                    background: C.dangerDim, border: `1px solid ${C.danger}`,
                    borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.text
                  }}>
                    <p style={{ marginBottom: 6 }}>
                      This station didn't report some fields for more than{" "}
                      <strong>{leadingGap.limitMinutes} minutes</strong> at the very start of
                      the collection window — too long a gap to safely fill in automatically.
                    </p>
                    {leadingGap.channels && (
                      <ul style={{ margin: "0 0 6px 18px", padding: 0 }}>
                        {Object.entries(leadingGap.channels).map(([ch, mins]) => (
                          <li key={ch}><code>{ch}</code>: {mins} min still missing</li>
                        ))}
                      </ul>
                    )}
                    <p style={{ color: C.textSub }}>
                      Try a longer collection window, or wait for more continuous data to accumulate.
                    </p>
                  </div>
                );
              }
              return <Err msg={previewError} />;
            })()
          ) : previewData ? (
            <>
              {previewData.mode === "model_input" && (
                <p style={{ fontSize: 11, color: C.textSub, marginBottom: 10 }}>
                  {previewData.context_window} min context · {previewData.forecast_horizon} min horizon
                </p>
              )}
              {previewData.sensor_backfill && Object.keys(previewData.sensor_backfill).length > 0 && (
                <div style={{
                  background: C.warnDim, border: `1px solid ${C.warn}`,
                  borderRadius: 8, padding: "8px 12px", fontSize: 11,
                  color: C.warn, marginBottom: 10
                }}>
                  ⚠ Some sensor channels had a gap at the very start of this window and were
                  backfilled from the next real reading (borrowed backward, not fabricated):{" "}
                  {Object.entries(previewData.sensor_backfill).map(([ch, mins]) => `${ch} (${mins} min)`).join(", ")}
                </div>
              )}
              <HeadTable title="Sensor data" rows={previewData.sensor_head} />
              <HeadTable title="Forecast data (merged forecast_start + forecast_end)" rows={previewData.forecast_head} />
            </>
          ) : (
            <p style={{ fontSize: 12, color: C.textSub, display: "flex", alignItems: "center", gap: 8 }}>
              {previewing ? <><Spinner /> Loading the current data…</> : "No preview yet — click \"Refresh preview\"."}
            </p>
          )}
        </Card>
      )}

      {/* ── SECTION 8: Export ── */}
      {(isReady || csvData) && (
        <Card>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>📦 Export parquet</p>
          <Err msg={error} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {isReady && activated && (
              <button
                className="primary"
                onClick={exportSensor}
                disabled={!!downloading || unmappedFields(sensorFields, sensorMap, sensorExcluded).length > 0}
                style={{ flex: 1 }}
              >
                {downloading === "sensor" ? <><Spinner /> Exporting…</> : `↓ sensor_${selected?.source}_${selected?.station_id}_${collectHours}h.parquet`}
              </button>
            )}
            {csvData && (
              <button
                onClick={exportCsv}
                disabled={!!downloading || unmappedFields(csvFields, csvMap, csvExcluded).length > 0}
                style={{ flex: 1 }}
              >
                {downloading === "csv" ? <><Spinner /> Exporting…</> : "↓ csv_mapped.parquet"}
              </button>
            )}
          </div>
          {isReady && (
            <p style={{ fontSize: 11, color: C.textSub, marginTop: 8 }}>
              Files will be named: inference_sensor_{selected?.source}_{selected?.station_id}_{collectHours}h_{"{timestamp}"}.parquet
            </p>
          )}
        </Card>
      )}

      {/* ── SECTION 9: Run inference once ── */}
      {isReady && activated && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>🧠 Step 3 — Run inference once</p>
          <p style={{ fontSize: 12, color: C.textSub, marginBottom: 12 }}>
            Runs a single inference right now against the currently mapped data. This is completely
            independent of the recurring schedule below — starting a schedule is optional, and running
            once here never starts one.
          </p>

          {weightsLoading && (
            <div style={{ fontSize: 12, color: C.textSub, display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Spinner /> Checking inference server for available weight sets…
            </div>
          )}
          {!weightsLoading && weightsError && <Err msg={weightsError} />}

          <div style={{ marginBottom: 10, maxWidth: 340 }}>
            <Label>Model type</Label>
            <div style={{ display: "flex", gap: 8 }}>
              {["with_forecast", "without_forecast"].map(m => (
                <button
                  key={m}
                  onClick={() => setWeightsMode(m)}
                  className={weightsMode === m ? "primary" : ""}
                  style={{ flex: 1 }}
                >
                  {m === "with_forecast" ? "With forecast" : "Without forecast"}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>
              Weight sets are trained per location — the dropdown below shows how far
              each one's training location is from {lat}, {lon} (the coordinates above),
              with the nearest one marked ★.
            </p>
          </div>

          {!weightsLoading && !weightsError && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 10, alignItems: "end", marginBottom: 6 }}>
              <div>
                <Label>Context window</Label>
                <select
                  value={contextWindow ?? ""}
                  onChange={e => {
                    const cw = +e.target.value;
                    setContextWindow(cw);
                    const match = weightSets.find(w => w.context_window === cw);
                    setWeightsId(match?.id || null);
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">— select —</option>
                  {contextWindows.map(cw => (
                    <option key={cw} value={cw} disabled={collectHours * 60 < cw}>
                      {cw} min (~{Math.ceil(cw / 60)}h){collectHours * 60 < cw ? " — collect longer first" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Weight set</Label>
                <select
                  value={weightsId ?? ""}
                  disabled={!contextWindow}
                  onChange={e => setWeightsId(e.target.value)}
                  style={{ width: "100%" }}
                >
                  <option value="">— select —</option>
                  {weightSets.filter(w => w.context_window === contextWindow).map(w => (
                    <option key={w.id} value={w.id}>
                      {w.nearest_location ? "★ " : ""}
                      fh {w.forecast_horizon}m · patch {w.patch_size} · stride {w.stride} · bs {w.batch_size}
                      {w.distance_km != null ? ` · ${w.distance_km} km away` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <button className="primary" onClick={runInference} disabled={inferring || !weightsId}>
                {inferring ? <><Spinner /> Running…</> : "▶ Run once"}
              </button>

            </div>
          )}

          {!weightsLoading && !weightsError && !weightsId && (
            <p style={{ fontSize: 11, color: C.warn, marginBottom: 12 }}>
              ⚠ Select a context window and weight set above to enable "Run once".
            </p>
          )}

          <Err msg={error} />

          {inferenceResult && (
            <div style={{
              background: C.successDim, border: `1px solid ${C.success}`,
              borderRadius: 8, padding: "10px 14px", fontSize: 12,
              display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8
            }}>
              <span>✅ Inference complete — {weightsId} ({inferenceResult.timestamp})</span>
              <button
                style={{ fontSize: 11, padding: "3px 10px" }}
                onClick={() => triggerDownload(
                  new Blob([JSON.stringify(inferenceResult.data, null, 2)], { type: "application/json" }),
                  `inference_result_${weightsId}_${inferenceResult.timestamp}.json`
                )}
              >
                ↓ Download result (.json)
              </button>
            </div>
          )}
        </Card>
      )}

      {/* ── SECTION 9.5: Automatic inference for this location (persistent, server-side) ── */}
      {isReady && activated && weightsId && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>🔁 Step 4 (optional) — Run automatically for this location</p>
          <p style={{ fontSize: 12, color: C.textSub, marginBottom: 12 }}>
            Registers a persistent schedule that <code>inference_runner.service</code> executes
            server-side — it keeps running even after you close this tab. Defaults to a 24h
            collection window and hourly runs; both adjustable below. View results any time in the
            Results tab.
          </p>

          <Err msg={schedulesError} />

          {scheduleForThisDevice ? (
            <div style={{
              background: C.successDim, border: `1px solid ${C.success}`, borderRadius: 8,
              padding: "10px 14px", fontSize: 12, display: "flex", justifyContent: "space-between",
              alignItems: "center", flexWrap: "wrap", gap: 8
            }}>
              <span>
                ✅ Running every <strong>{scheduleForThisDevice.frequency_minutes} min</strong>,{" "}
                {scheduleForThisDevice.collect_hours}h collection window
                {scheduleForThisDevice.last_run_at && <> · last run: {scheduleForThisDevice.last_run_at.slice(0, 16).replace("T", " ")}</>}
                {scheduleForThisDevice.last_error && <span style={{ color: C.danger }}> · last error: {scheduleForThisDevice.last_error}</span>}
              </span>
              <button onClick={() => stopScheduleBackend(scheduleForThisDevice.id)}
                style={{ fontSize: 11, padding: "3px 10px", color: C.danger, borderColor: C.danger }}>
                ⏹ Stop schedule
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end", marginBottom: 6 }}>
                <div>
                  <Label>Run every (minutes)</Label>
                  <input type="number" min={5} value={scheduleFrequencyMinutes}
                    onChange={e => setScheduleFrequencyMinutes(+e.target.value)} style={{ width: "100%" }} />
                </div>
                <div>
                  <Label>Collection window (hours)</Label>
                  <input type="number" min={1} value={scheduleCollectHours}
                    onChange={e => setScheduleCollectHours(+e.target.value)} style={{ width: "100%" }} />
                </div>
                <button className="primary" onClick={createSchedule} disabled={creatingSchedule}>
                  {creatingSchedule ? <><Spinner /> Creating…</> : "▶ Start automatic runs"}
                </button>
              </div>
              {scheduleCollectHours * 60 < (weightSets.find(w => w.id === weightsId)?.context_window || 0) && (
                <p style={{ fontSize: 11, color: C.warn }}>
                  ⚠ This weight set needs at least{" "}
                  {Math.ceil((weightSets.find(w => w.id === weightsId)?.context_window || 0) / 60)}h —
                  increase the collection window above.
                </p>
              )}
            </>
          )}
        </Card>
      )}

      {/* ── SECTION 10: Postprocessing dashboard ── */}
      {inferenceResult && (() => {
        const d = inferenceResult.data;
        const channels = d.channels || [];
        // The channel selector only offers channels that exist in BOTH
        // predictions and the baseline forecast — predictions can include
        // sensor-only channels (e.g. rh, sp_pa from the sensor side, tagged
        // "<name>__sensor" by inference_server.py) that the raw NWP
        // baseline never has, since baseline is drawn purely from the
        // forecast product itself. Comparing "model vs raw forecast" only
        // makes sense where both actually have a value.
        const baseline = d.baseline_forecast;
        const baselineChannels = baseline?.channels || [];
        const comparableChannels = channels.filter(c => baselineChannels.includes(c));
        const chan = dashboardChannel && comparableChannels.includes(dashboardChannel)
          ? dashboardChannel
          : comparableChannels[0];
        // Two SEPARATE indices — predictions and baseline are independent
        // channel lists now that predictions can be longer (sensor+forecast)
        // than baseline (forecast-only). Never assume they share a position.
        const predChIdx = channels.indexOf(chan);
        const baselineChIdx = baselineChannels.indexOf(chan);

        // Prefer the native-cadence downsampled series — showing 60
        // near-identical 1-minute steps for a station that only reports
        // every 10 minutes is misleading. Falls back to the full 1-per-min
        // grid if native_interval_minutes couldn't be detected (older
        // inference_server.py, or genuinely ~1-min-native station).
        const usingNative = !!d.predictions_native;
        const modelSeries = usingNative
          ? (d.predictions_native?.[predChIdx] || [])
          : (predChIdx >= 0 ? (d.predictions?.[predChIdx] || []) : []);

        const fullBaselineSeries = baselineChIdx >= 0 ? (baseline?.values?.[baselineChIdx] || []) : [];
        // Downsample the baseline the same way so both series line up on
        // the same x-axis when native cadence is in play.
        const baselineSeries = usingNative && d.native_interval_minutes
          ? fullBaselineSeries.filter((_, i) => i % Math.max(1, Math.round(d.native_interval_minutes)) === 0)
          : fullBaselineSeries;

        return (
          <Card style={{ marginTop: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>📈 Postprocessing — model vs raw forecast</p>
            <p style={{ fontSize: 12, color: C.textSub, marginBottom: 6 }}>
              Model's own {d.forecast_horizon}-minute prediction plotted against the raw NWP forecast
              (from forecast_end) for the same channel and the same future window — a quick eyeball
              baseline comparison, not a formal accuracy metric.
            </p>
            {channels.length > 0 && d.shape?.[0] != null && channels.length !== d.shape[0] && (
              <p style={{ fontSize: 11, color: C.warn, marginBottom: 10 }}>
                ⚠ This run's channel list ({channels.length}) doesn't match its prediction row
                count ({d.shape[0]}) — it was likely produced before the channel-labeling fix, so
                some series in this dropdown may be mislabeled.
              </p>
            )}
            {d.native_interval_minutes != null && (
              <p style={{ fontSize: 11, color: C.textSub, marginBottom: 10 }}>
                Station reports every ~{d.native_interval_minutes} min — plotting at that cadence
                ({usingNative ? modelSeries.length : fullBaselineSeries.length} points) instead of
                the full 1-per-minute grid, since forward-filled minutes in between don't reflect
                any real change in the input.
              </p>
            )}

            <div style={{ marginBottom: 10, maxWidth: 260 }}>
              <Label>Channel</Label>
              <select value={chan || ""} onChange={e => setDashboardChannel(e.target.value)} style={{ width: "100%" }}>
                {comparableChannels.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {baseline && baseline.available_horizon < baseline.requested_horizon && (
              <p style={{ fontSize: 11, color: C.warn, marginBottom: 10 }}>
                Only {baseline.available_horizon}/{baseline.requested_horizon} min of raw forecast baseline
                available after collection ended — forecast_end may need a longer `days` window.
              </p>
            )}

            <MiniLineChart
              seriesA={modelSeries} seriesB={baselineSeries}
              labelA="Model forecast" labelB="NWP forecast"
              colorA={C.success} colorB={C.accent}
            />
          </Card>
        );
      })()}
    </div>
  );
}